<script lang="ts">
export default {
  name: 'UILayer',
};
</script>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import ExportButton from './ExportButton.vue';
import ExportList from './ExportList.vue';
import { TowerInfo, UIColors } from '@/views/tower_visualizer/types/TowerVisualizerPage';
import { SaveTowerRequest } from '@/services/v2/TowerService';
import { towerService } from '@/services/v2/TowerService';
import { customEvents } from '@/store/CustomEvents';
import { useRouter } from 'vue-router';
import { ProgramTower } from '@/types/tower/v2/Tower';
import Zoom from '../tower/v2/Zoom.vue';
import { useStore } from 'vuex';
import { AppState } from '@/store';
import CanvasUtils from '@/utilities/tower/v2/Canvas';
import MultiTowerSaveModal from './MultiTowerSaveModal.vue';
import { TowerType } from '../tower_dashboard/types/tower-dashboard';
import { TowerCardData } from '../tower_dashboard/types/tower-dashboard';
import { ChevronsLeft } from '@lucide/vue';
import { IsUserAClient } from '@/utilities/AuthUtils';

defineProps<{
  uiColors: UIColors;
}>();

const showSaveError = ref(false);
const showCreateMultiTowerModal = ref(false);
const towerInfo = inject<TowerInfo>('towerInfo');
const router = useRouter();
const store = useStore<AppState>();

const activeTower = computed(() => {
  return towerInfo?.selectedTower.value;
});

const isNewTower = computed(() => {
  return !towerInfo?.tower.value.towerId;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

async function handleMultiTowerSave(): Promise<void> {
  if (isNewTower.value) {
    showCreateMultiTowerModal.value = true;
    return;
  }

  if (!towerInfo?.towerChanged) return;
  towerInfo.canvas.value.setZoom(store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM);

  try {
    const request = {
      tower: {
        ...towerInfo.tower.value,
        towerObject: JSON.stringify(towerInfo.canvas.value?.toJSON(['zoomLevel'])),
      } as ProgramTower,
    } as SaveTowerRequest;

    await towerService.saveTower(request);
    dispatchEvent(new CustomEvent(customEvents.towerVisualizerSetCanvasToCurrent));
  } catch (e) {
    console.error(e);
    showSaveError.value = true;

    setTimeout(() => {
      showSaveError.value = false;
    }, 3000);
  }
}

async function handleTowerModalSaveClick(versionLabel: string): Promise<void> {
  if (!towerInfo?.towerChanged) return;
  towerInfo.canvas.value.setZoom(store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM);

  try {
    const request = {
      action: 'new',
      tower: {
        towerTypeId: TowerType.MultiTower,
        towerObject: JSON.stringify(towerInfo.canvas.value?.toJSON(['zoomLevel'])),
        versionLabel,
        accountId: towerInfo.accountId.value,
      } as ProgramTower,
    } as SaveTowerRequest;

    const savedTower = await towerService.saveTower(request);
    savedTower.userId = store.state.user?.user.id || 0;
    dispatchEvent(new CustomEvent(customEvents.towerVisualizerSetCanvasToCurrent));
    showCreateMultiTowerModal.value = false;

    towerInfo.tower.value = savedTower;

    router.replace(`${savedTower.towerId}`);
  } catch (e) {
    console.error(e);
    showSaveError.value = true;

    setTimeout(() => {
      showSaveError.value = false;
    }, 3000);
  }
}

function getTowerName(towerCard: TowerCardData): string {
  if (towerCard.towerTypeId === TowerType.MultiTower) {
    return 'Multi-Tower';
  } else if (towerCard.program) {
    return towerCard.program.name;
  } else if (towerCard.towerName) {
    return towerCard.towerName;
  }

  return '';
}
</script>

<template>
  <div
    class="layer-ui opacity-95"
    :class="towerInfo?.towerChanged.value && !isUserAClient ? 'border-4 border-solid border-red-500' : ''"
  >
    <div class="pt-1 flex row-start-1 col-start-1">
      <div
        @click="router.back()"
        class="h-fit bg-base-200 border border-solid border-gray-200 flex items-center justify-center cursor-pointer pointer-events-auto border-box rounded-md shadow-lg hover:bg-gray-300 transition-all px-1 py-1 m-2"
      >
        <ChevronsLeft :size="19" />
        <span class="mr-2">Back</span>
      </div>
      <div v-if="activeTower" class="flex flex-col pl-2 h-fit items-center">
        <span v-if="activeTower.versionLabel" class="font-bold">{{ activeTower.versionLabel }}</span>
        <span class="text-xs">{{ getTowerName(activeTower) }}</span>
      </div>
    </div>
    <button
      v-if="towerInfo?.towerChanged.value && !isUserAClient"
      class="pointer-events-auto absolute btn rounded-t-none rounded-r-none text-white cursor-pointer right-0 p-1 bg-red-500"
      @click="handleMultiTowerSave"
    >
      SAVE
    </button>
    <Zoom 
      class="w-40 pr-2 col-start-1"
      bg-color="base-200"
      border-color="gray-300"
    />
    <div class="col-start-2 row-start-2 flex flex-col w-full h-full justify-end">
      <ExportList class="w-40 pr-2 self-end" />
      <ExportButton />
    </div>
    <div class="toast" :class="{ 'invisible': !showSaveError }">
      <div class="alert alert-error">
        <span>Error saving the multi-tower</span>
      </div>
    </div>
    <MultiTowerSaveModal
      :show="showCreateMultiTowerModal"
      @cancel="showCreateMultiTowerModal = false"
      @save="handleTowerModalSaveClick"
    />
  </div>
</template>

<style scoped>
.layer-ui {
  display: grid;
  justify-items: start;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  position: absolute;
  height: 100%;
  width: 100%;
  pointer-events: none;
  z-index: var(--zindex-layerui);
}
</style>
